'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import DestinationSelector from './DestinationSelector';
import ComparisonMatrix from './ComparisonMatrix';
import type { VisaMatrixAnalysisResponse } from '@/app/api/anthropic/visa-matrix-analysis/route';
import type { ApiResponse } from '@/lib/types/anthropic';

export interface DestinationInput {
  id: string;
  country: string;
  nationality: string;
  travelPurpose: string;
  stayDuration: number;
}

export default function VisaMatrixInteractive() {
  const [destinations, setDestinations] = useState<DestinationInput[]>([]);
  const [matrixData, setMatrixData] = useState<VisaMatrixAnalysisResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAddDestination = (destination: DestinationInput) => {
    if (destinations.length >= 5) {
      setError('Maximum 5 destinations can be compared at once.');
      return;
    }
    setDestinations([...destinations, destination]);
    setError(null);
  };

  const handleRemoveDestination = (id: string) => {
    setDestinations(destinations.filter(d => d.id !== id));
    if (destinations.length <= 1) {
      setMatrixData(null);
    }
  };

  const handleAnalyze = async () => {
    if (destinations.length < 2) {
      setError('Please add at least 2 destinations to compare.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await fetch('/api/anthropic/visa-matrix-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ destinations }),
      });

      const data: ApiResponse<VisaMatrixAnalysisResponse> = await res.json();

      if (data.success) {
        setMatrixData(data.data);
      } else {
        setError(data.error.message);
        if (!data.error.isInternal) {
          console.error('Error analyzing visa requirements:', data.error.message);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to analyze visa requirements.';
      setError(errorMessage);
      console.error('Error analyzing visa requirements:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl mb-6 shadow-xl">
            <Icon name="GlobeAltIcon" size={40} className="text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
            Visa Requirements Matrix
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Compare visa requirements, document checklists, processing times, and approval odds across multiple destinations side-by-side
          </p>
        </div>

        {/* Destination Selector */}
        <DestinationSelector
          destinations={destinations}
          onAddDestination={handleAddDestination}
          onRemoveDestination={handleRemoveDestination}
        />

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Icon name="ExclamationCircleIcon" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-red-900">Error</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Analyze Button */}
        {destinations.length >= 2 && !matrixData && (
          <div className="text-center mb-8">
            <button
              onClick={handleAnalyze}
              disabled={loading}
              className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-black text-lg rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  <span>Analyzing Visa Requirements...</span>
                </>
              ) : (
                <>
                  <Icon name="SparklesIcon" size={24} />
                  <span>Compare Visa Requirements with AI</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-6"></div>
            <p className="text-lg font-bold text-gray-900 mb-2">Claude AI is analyzing visa requirements...</p>
            <p className="text-gray-600">Comparing document checklists, processing times, and approval odds</p>
          </div>
        )}

        {/* Comparison Matrix */}
        {matrixData && !loading && (
          <ComparisonMatrix data={matrixData} destinations={destinations} />
        )}

        {/* Info Cards */}
        {!matrixData && destinations.length === 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-blue-100">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Icon name="DocumentCheckIcon" size={24} className="text-blue-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Document Checklists</h3>
              <p className="text-sm text-gray-600">
                Complete list of required documents for each destination with validity requirements
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-purple-100">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Icon name="ClockIcon" size={24} className="text-purple-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Processing Times</h3>
              <p className="text-sm text-gray-600">
                Current processing times and appointment availability for each embassy
              </p>
            </div>
            <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-pink-100">
              <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center mb-4">
                <Icon name="ChartBarIcon" size={24} className="text-pink-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Approval Odds</h3>
              <p className="text-sm text-gray-600">
                AI-powered approval probability analysis based on your profile and destination
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}